import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import type { JournalEntry, TripPhoto } from '../../types';
import { useDateLocale } from '../../utils/dateLocale';
import { UserAvatar } from '../ui/UserAvatar';
import { Icon, Lock, Pencil, Trash2 } from '../ui/Icon';
import PhotoGrid, { type PhotoGridSize } from './PhotoGrid';

interface Props {
  entry: JournalEntry;
  photos: TripPhoto[];
  canEditEntry: boolean;
  canDeleteEntry: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onOpenPhoto: (photo: TripPhoto) => void;
  photoSize?: PhotoGridSize;
}

export default function JournalEntryCard({ entry, photos, canEditEntry, canDeleteEntry, onEdit, onDelete, onOpenPhoto, photoSize = 'compact' }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const name = entry.author.displayName || entry.author.username;

  return (
    <article className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-4 space-y-3">
      <header className="flex items-center gap-2.5">
        <UserAvatar name={name} avatarUrl={entry.author.avatarUrl} size={30} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{name}</p>
          <p className="flex items-center gap-1 text-[11px] text-slate-400">
            {format(parseISO(entry.createdAt), 'PPp', { locale: dateLocale })}
            {!entry.shared && (
              <span className="inline-flex items-center gap-0.5" title={t('journal.groupOnlyHint')}>
                · <Icon as={Lock} size={10} /> {t('journal.groupOnly')}
              </span>
            )}
          </p>
        </div>
        {canEditEntry && (
          <button onClick={onEdit} className="btn-icon w-8 h-8 text-slate-500" title={t('common.edit')} aria-label={t('common.edit')}>
            <Icon as={Pencil} size={14} />
          </button>
        )}
        {canDeleteEntry && (
          <button onClick={onDelete} className="btn-icon w-8 h-8 text-red-500" title={t('common.delete')} aria-label={t('common.delete')}>
            <Icon as={Trash2} size={14} />
          </button>
        )}
      </header>
      <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200 whitespace-pre-line">{entry.body}</p>
      <PhotoGrid photos={photos} onOpen={onOpenPhoto} size={photoSize} />
    </article>
  );
}
